import React, { useState, useRef, useEffect } from "react";
import { css } from "@emotion/react";
import Transition from "react-transition-group/Transition";
import { isMobile } from "main/javascripts/utils/DeviceUtil";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { GuestPicker } from "./GuestPicker";
import { Button } from "../../button/Button";
import { borderColor } from "main/javascripts/styles/base/colorStyle";
import { useTranslation } from "react-i18next";

const enterDuration = 360;
const exitDelay = 100;
const exitDuration: number = enterDuration + exitDelay;

interface IProps {
  namespace: string;
  groupName?: string;
  displayedPicker: boolean;
  onUpdate(): void;
  onComplete(): void;
  hidePicker(): void;
  styles?: any;
}

// バリデーションやエラー表示をやりやすくするために
// react hook formに依存している
export const DropdownGuestPicker: React.FC<IProps> = (
  props: IProps
): React.ReactElement<IProps> => {
  const {
    namespace,
    groupName,
    styles = {},
    displayedPicker,
    onUpdate,
    onComplete,
    hidePicker,
  } = props;
  const { exBlockStyle } = styles;
  const { t } = useTranslation(["button"]);

  const [isDisplayed, setIsDisplayed] = useState(false);
  const ref = useRef(null);
  const isDisplayedRef = useRef(false);
  isDisplayedRef.current = isDisplayed;

  useEffect(() => {
    const evt: string = isMobile() ? "touchend" : "click";
    document.addEventListener(evt, clickOutsideHandler, false);
    return () => {
      document.removeEventListener(evt, clickOutsideHandler, false);
    };
  }, []);

  const clickOutsideHandler: (e: any) => void = (e: any) => {
    if (
      isDisplayedRef.current &&
      ref.current &&
      !ref.current.contains(e.target)
    ) {
      if (checkIsMounted()) {
        hidePicker();
      }
    }
  };

  const checkIsMounted = (): boolean => {
    return ref.current !== null;
  };

  const onEntered: () => void = () => {
    setIsDisplayed(true);
  };

  const onExit: () => void = () => {
    setIsDisplayed(false);
  };

  const onPickerComplete = (e): void => {
    e.preventDefault();
    onComplete();
  };

  return (
    <Transition
      timeout={{
        enter: enterDuration,
        exit: exitDuration,
      }}
      in={displayedPicker}
      unmountOnExit={true}
      onEntered={onEntered}
      onExit={onExit}
      nodeRef={ref}
    >
      {(state: any): JSX.Element => (
        <div
          css={[blockStyle, exBlockStyle]}
          className="guestPicker"
          style={{
            ...defaultStyle,
            ...transitionStyles[state],
          }}
          ref={ref}
        >
          <div css={pickerBlockStyle}>
            <GuestPicker
              namespace={namespace}
              groupName={groupName}
              onUpdate={onUpdate}
            />
          </div>
          <div css={footerStyle}>
            <div css={buttonBlockStyle}>
              <Button
                type="button"
                styleKey="buttonSmall"
                onClick={onPickerComplete}
              >
                {t("common.set")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Transition>
  );
};

const blockStyle = css`
  position: absolute;
  right: 0;
  height: auto;
  box-sizing: border-box;
  width: auto;
  min-width: 20rem;
  background-color: white;
  border-radius: ${borderRadius.normal};
  margin: ${space.atom} 0 0;
  padding: ${space.atom};
  box-shadow: 0 6px 30px rgba(40, 60, 90, 0.1);
  z-index: 2;
`;
const pickerBlockStyle = css`
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding: ${space.atom};
  max-height: 600px;
`;
const footerStyle = css`
  margin: 0 -${space.atom} -${space.atom};
  padding: ${space.atom};
  box-sizing: border-box;
  min-height: 4.5rem;
  border-top: 1px solid ${borderColor.primaryLightColor};
`;
const buttonBlockStyle = css`
  padding: ${space.atom};
`;

export const defaultStyle: any = {
  transition: `all ${enterDuration}ms ease`,
  opacity: 0,
  transform: "translateY(-16px)",
  touchAction: "manipulation",
};

export const transitionStyles: any = {
  entering: {
    opacity: 0,
    transform: "translateY(-16px)",
  },
  entered: {
    opacity: 1,
    transform: "translateY(0)",
  },
  exiting: {
    transition: `all ${enterDuration}ms ease ${exitDelay}ms`,
  },
  exited: {
    display: "none",
  },
};
